/** Provider sources that must never appear in the supplier PDF export. */
export const EXCLUDED_KYB_REPORT_SOURCES = [
    "company_info",
    "credit_safe",
];

/** Provider sources with dedicated PDF rendering. */
export const SUPPORTED_KYB_REPORT_SOURCES = [
    "dun_brad_street",
    "dun_brad_street_enterprise",
    "handelsregister",
];

const KYB_REPORT_SOURCE_LABELS = {
    dun_brad_street: "Dun & Bradstreet",
    dun_brad_street_enterprise: "Dun & Bradstreet Enterprise",
    handelsregister: "Handelsregister",
};

export const EXTERNAL_PROVIDER_REPORTS_SECTION_ID = "external-provider-reports";

export const resolveKybReportSource = (report) => {
    if (!report || typeof report !== "object") {
        return null;
    }

    return (
        report?.company?.source
        || report?.source
        || report?.provider
        || null
    );
};

export const getKybReportDisplayTitle = (report, index = 0) => {
    const source = resolveKybReportSource(report);

    if (source && KYB_REPORT_SOURCE_LABELS[source]) {
        return KYB_REPORT_SOURCE_LABELS[source];
    }

    if (report?.title) {
        return report.title;
    }

    if (report?.company?.name) {
        return report.company.name;
    }

    if (source) {
        return source;
    }

    return `Provider Report ${index + 1}`;
};

export const getKybReportChildId = (index) => `kyb-report-${index}`;

/** Matches sectionRegistry child anchors: `toc-${child.id}`. */
export const getKybReportAnchorId = (index) => `toc-${getKybReportChildId(index)}`;

export const isSupportedKybReportSource = (source) => (
    !!source && SUPPORTED_KYB_REPORT_SOURCES.includes(source)
);

export const buildKybReportSectionChildren = (kybReports = []) => {
    if (!Array.isArray(kybReports) || kybReports.length === 0) {
        return [];
    }

    return kybReports.map((report, index) => ({
        id: getKybReportChildId(index),
        title: getKybReportDisplayTitle(report, index),
    }));
};

export const normalizeKybReportsPayload = (payload) => {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (Array.isArray(payload?.reports)) {
        return payload.reports;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload?.items)) {
        return payload.items;
    }

    return [];
};

export const filterKybReportsForExport = (reports = []) => {
    if (!Array.isArray(reports)) {
        return [];
    }

    return reports.filter((report) => {
        const source = resolveKybReportSource(report);

        if (!source) {
            return true;
        }

        return !EXCLUDED_KYB_REPORT_SOURCES.includes(source);
    });
};

/**
 * Normalize a single /reports item into the KYB details shape used by driver exports:
 * { company, financial_summary, management_team, company_shareholders, ... }
 */
export const extractKybReportDetails = (report) => {
    if (!report || typeof report !== "object") {
        return null;
    }

    if (report.company || report.financial_summary || report.management_team) {
        return report;
    }

    if (report.data && typeof report.data === "object") {
        return extractKybReportDetails(report.data);
    }

    if (report.details && typeof report.details === "object") {
        return extractKybReportDetails(report.details);
    }

    if (report.report && typeof report.report === "object") {
        return extractKybReportDetails(report.report);
    }

    return report;
};
